/**
 * Qinggong in the bout on screen (lane QINGGONG-POSES, duel v3 P1.5): which duel-move motion a fighter shows
 * (motions/duelMoves.ts) and how far through it, from the rules' own state only:
 * - steps: systems/duelMove.ts `latest` (the kind and time of the latest step); the first half of the keys is the travel
 *   (`COMBAT.dashDuration`), the second half the stop (systems/duelRecovery.ts `info`, tagged `step:<kind>`);
 * - the leap: the take-off (the first moments of a first jump, the feet held on the ground through the crouch), the arc
 *   by the vertical speed and the height, the air dash (a step taken in the air), the dive (systems/duelAir.ts `dive`)
 *   and its landing (the blow's own time, or the `dive-miss` stop), the landing stop (tag `land`);
 * - the slide: systems/slide.ts `slideOf` (its own time), the stop when it runs out (tag `slide`), and the slide strike
 *   (a blow that starts while sliding or as the slide ends: the blow's own time, or the guarded stop if it is longer).
 * Lengths come from the rules, so balance changes need no art change. Bout only: the open world keeps its own
 * (render/qinggongAnim.ts).
 *
 * Every frame is drawn facing the foe whichever way the fighter travels: a back step is a hop back with the blade still
 * on the foe, not a turn and run. The renderer whirls the view by `turn` heading steps, as it does for a receive motion.
 * A fighter at ease (not yet in the exchange) plays the at-ease variant, so the arms blend from the stance it stood in.
 */
import {
  COMBAT,
  duelAirOf,
  duelMoveOf,
  duelRecoveryOf,
  MOVEMENT,
  screenHeading,
  slideOf,
  type Entity,
  type World,
} from '@nine/shared';
import type { MotionId } from './motions';
import type { DuelMotionId } from './motions/duelMoves';

/** What the renderer draws: a motion, how far through it, the view turned by `turn` heading steps, lifted by `lift`. */
export interface DuelMoveFrame {
  id: MotionId;
  u: number;
  turn: number;
  rot: number;
  lift: number;
}

/** Seconds of the take-off crouch and spring (as the open world's, render/qinggongAnim.ts `MOVE_TIME.takeoff`). */
const TAKEOFF = 0.16;
/** Height (px) where the legs start reaching for the ground. */
const REACH_HEIGHT = 26;

/** Per fighter: each side step's side, fixed when it starts; whether it was diving, and when its dive landed. */
interface Memo {
  stepAt: number;
  right: boolean;
  diving: boolean;
  diveEnd: number;
  /** When it was last on the ground (a take-off only straight off it, not a second jump). */
  groundAt: number;
  /** Sliding last frame, when the slide ended; swinging last frame, when a slide strike started and its blow's time. */
  sliding: boolean;
  slideEnd: number;
  swinging: boolean;
  strikeAt: number;
  strikeLen: number;
}
const memo = new Map<number, Memo>();

const clamp01 = (k: number) => Math.max(0, Math.min(1, k));
const smooth = (k: number) => {
  const c = clamp01(k);
  return c * c * (3 - 2 * c);
};

/** The motion, its progress and lift for a fighter in a bout this frame (null: none of ours). */
function pick(w: World, e: Entity, m: Memo): { id: DuelMotionId; u: number; lift?: number; ease?: boolean } | null {
  const sys = duelMoveOf(w)!;
  const s = sys.latest(e.id);
  // สลัดหลุด breaks out of the hit: the blow's own flinch (hitT) gives way to it.
  const escaping = s?.kind === 'escape' && w.time - s.at < 1;
  if ((e.hitT > 0 && !escaping) || e.stunT > 0 || e.cast || e.blocking || e.spinT > 0) return null;
  const at = (id: DuelMotionId, u: number, lift?: number) => ({ id, u: clamp01(u), ...(lift ? { lift } : {}) });
  // The dive, then its landing: the blade driven down, held, the body hauled up (the blow's own time, or the stop).
  const dive = duelAirOf(w)?.dive(e.id);
  if (dive) return at('qsDive', dive.k);
  const sinceDive = w.time - m.diveEnd;
  if (sinceDive >= 0 && sinceDive < 1) {
    const r = duelRecoveryOf(w)?.info(e.id);
    const len = r?.skillId === 'dive-miss' ? Math.max(0.25, r.len) : 0.25;
    if (sinceDive < len) return at('qsDiveLand', sinceDive / len);
  }
  // The slide strike, over the blow's own time (or the guarded stop, if that ends later).
  const sinceStrike = w.time - m.strikeAt;
  if (sinceStrike >= 0 && sinceStrike < 1.5) {
    const g = duelRecoveryOf(w)?.info(e.id);
    const end = Math.max(m.strikeLen, g?.skillId === 'slide-strike-guarded' ? sinceStrike - g.t + g.len : 0);
    if (sinceStrike < end) return at('qsSlideStrike', sinceStrike / end);
  }
  if (e.swingT > 0) return null;
  // The slide, by its own time; the stop when it ran out.
  const sl = slideOf(e);
  if (sl) return { ...at('qsSlide', sl.t / sl.time), ease: true };
  const rs = duelRecoveryOf(w)?.info(e.id);
  if (rs?.skillId === 'slide') return { ...at('qsSlideStop', rs.t / Math.max(1e-3, rs.len)), ease: true };
  if (e.z > 0.5) {
    // A step taken in the air: the air dash.
    if (e.dashT > 0 && s?.kind === 'air' && w.time - s.at < COMBAT.dashDuration)
      return at('qsAirDash', (w.time - s.at) / COMBAT.dashDuration);
    // (A second jump in the bout plays the same arc: the jump count is not reliable for the rival, whose count is
    // never given back on landing, and the open world's somersault would turn the fighter away from the foe.)
    if (e.vz > 0 && w.time - m.groundAt < TAKEOFF + 0.05) {
      const since = (MOVEMENT.jumpVelocity - e.vz) / MOVEMENT.gravity;
      // The feet held on the ground through the crouch, catching up with the body as it springs.
      if (since >= 0 && since < TAKEOFF)
        return { ...at('qsTakeoff', since / TAKEOFF, -e.z * (1 - smooth(since / TAKEOFF))), ease: true };
    }
    // The arc: rising until the vertical speed drops, tucked at the top, falling, then the legs reaching down.
    const uv = clamp01((45 - e.vz) / 90);
    return at('qsAir', uv < 1 ? (uv * 2) / 3 : 2 / 3 + Math.max(0, 1 - e.z / REACH_HEIGHT) / 3);
  }
  const r = duelRecoveryOf(w)?.info(e.id);
  // The landing stop.
  if (r?.skillId === 'land') return { ...at('qsLand', r.t / Math.max(1e-3, r.len)), ease: true };
  if (!s || s.kind === 'air') return null;
  const since = w.time - s.at;
  if (since < 0) return null;
  let u: number;
  if (since < COMBAT.dashDuration || e.dashT > 0) u = 0.5 * Math.min(1, since / COMBAT.dashDuration);
  else if (r && r.skillId === `step:${s.kind}`) u = 0.5 + 0.5 * Math.min(1, r.t / Math.max(1e-3, r.len));
  // Held on the landing key for the tick between the travel's end and the stop's start.
  else if (since < COMBAT.dashDuration + 0.05) u = 0.5;
  else return null;
  if (s.kind === 'escape') return { ...at('qsEscape', u), ease: true };
  if (s.kind !== 'side') return { ...at(s.kind === 'back' ? 'qsBack' : 'qsFwd', u), ease: true };
  if (m.stepAt !== s.at) {
    const foe = sys.foeOf(e.id);
    if (!foe) return null;
    const fx = foe.x - e.x;
    const fy = foe.y - e.y;
    const fl = Math.hypot(fx, fy) || 1;
    // Facing the foe along (ux, uy), the fighter's right is (−uy, ux) on the map (y runs south).
    m.right = e.dashVX * -(fy / fl) + e.dashVY * (fx / fl) > 0;
    m.stepAt = s.at;
  }
  return { ...at(m.right ? 'qsSideR' : 'qsSideL', u), ease: true };
}

/**
 * The duel-move frame of a fighter in a bout, or null (not in a bout, nothing of ours under way, or something else owns
 * the frame: a blow, a hit, a guard, a cast). `heading` is the renderer's screen heading of a world direction.
 */
export function duelMoveFrame(
  w: World,
  e: Entity,
  heading: (v: { heading: number; dirX: number; dirY: number }) => number,
): DuelMoveFrame | null {
  const sys = duelMoveOf(w);
  if (!sys || !sys.inBout(e.id) || e.dead) return null;
  let m = memo.get(e.id);
  if (!m) memo.set(e.id, (m = {
      stepAt: -1,
      right: true,
      diving: false,
      diveEnd: -9,
      groundAt: -9,
      sliding: false,
      slideEnd: -9,
      swinging: false,
      strikeAt: -9,
      strikeLen: 0,
    }));
  const diving = !!duelAirOf(w)?.dive(e.id);
  if (m.diving && !diving) m.diveEnd = w.time;
  m.diving = diving;
  if (e.z <= 0.5) m.groundAt = w.time;
  const sliding = !!slideOf(e);
  if (m.sliding && !sliding) m.slideEnd = w.time;
  m.sliding = sliding;
  // A blow started while sliding, or as the slide ends (the move that cut it short): the slide strike.
  if (e.swingT > 0 && !m.swinging && (sliding || w.time - m.slideEnd < 0.06)) {
    m.strikeAt = w.time;
    m.strikeLen = e.swingT;
  }
  m.swinging = e.swingT > 0;
  const p = pick(w, e, m);
  if (!p) return null;
  const foe = sys.foeOf(e.id);
  if (!foe) return null;
  const fx = foe.x - e.x;
  const fy = foe.y - e.y;
  const fl = Math.hypot(fx, fy);
  if (fl < 1e-3) return null;
  // At ease (not yet in the exchange): the variant that starts from and ends in standing at ease.
  const id = (p.ease && !(e.combatT > 0) ? `${p.id}E` : p.id) as MotionId;
  const toFoe = heading({ heading: screenHeading(fx, fy), dirX: fx / fl, dirY: fy / fl });
  return { id, u: p.u, turn: toFoe - heading(e), rot: 0, lift: p.lift ?? 0 };
}
